import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, LayoutGrid, QrCode, Sparkles, TrendingUp, Users } from "lucide-react";
import { Orb } from "@/components/Orb";
import { PfizerLogo } from "@/components/PfizerLogo";
import { QRBlock } from "@/components/QRBlock";
import { getStats, listTeammates } from "@/lib/api";
import type { Stats, TeammateSummary } from "@/lib/types";
import { cn } from "@/lib/cn";

type TabId = "qr" | "feed" | "orb" | "marketplace" | "stats";
type SessionKey = "all" | "1" | "2" | "3";

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "qr", label: "Scan", icon: QrCode },
  { id: "feed", label: "Live feed", icon: Activity },
  { id: "orb", label: "Presence", icon: Sparkles },
  { id: "marketplace", label: "Marketplace", icon: LayoutGrid },
  { id: "stats", label: "Results", icon: TrendingUp },
];

const SESSION_KEYS: SessionKey[] = ["1", "2", "3", "all"];

export default function Stage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>((params.get("tab") as TabId) || "qr");
  const [session, setSession] = useState<SessionKey>(
    (params.get("s") as SessionKey) || "1",
  );
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<TeammateSummary[]>([]);
  const [pulseKey, setPulseKey] = useState(0);
  const lastCountRef = useRef(0);

  const refresh = useCallback(async () => {
    const sid = session === "all" ? undefined : (Number(session) as number);
    try {
      const [s, list] = await Promise.all([
        getStats(session === "all" ? "all" : Number(session)),
        listTeammates({ session: sid, limit: 60 }),
      ]);
      setStats(s);
      setRecent(list);
      if (lastCountRef.current && s.total > lastCountRef.current) {
        setPulseKey((k) => k + 1);
      }
      lastCountRef.current = s.total;
    } catch {
      /* keep last state */
    }
  }, [session]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    next.set("s", session);
    setParams(next, { replace: true });
  }, [tab, session, params, setParams]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "1") setTab("qr");
      else if (e.key === "2") setTab("feed");
      else if (e.key === "3") setTab("orb");
      else if (e.key === "4") setTab("marketplace");
      else if (e.key === "5") setTab("stats");
      else if (e.key.toLowerCase() === "s") {
        setSession((prev) => {
          const idx = SESSION_KEYS.indexOf(prev);
          return SESSION_KEYS[(idx + 1) % SESSION_KEYS.length];
        });
      } else if (e.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://pfizer.ruicosta.ai";
  const qrUrl = session === "all" ? `${origin}/?s=1` : `${origin}/?s=${session}`;

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <AmbientBackground pulseKey={pulseKey} />
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-[1440px] flex-col px-10 py-8">
        <TopBar
          total={stats?.total ?? 0}
          session={session}
          onSessionChange={setSession}
          pulseKey={pulseKey}
        />

        <div className="mt-8 flex flex-1 items-stretch">
          <AnimatePresence mode="wait">
            {tab === "qr" && <QRTab key="qr" url={qrUrl} session={session} />}
            {tab === "feed" && <FeedTab key="feed" recent={recent} />}
            {tab === "orb" && <OrbTab key="orb" pulseKey={pulseKey} total={stats?.total ?? 0} />}
            {tab === "marketplace" && <MarketplaceTab key="mkt" recent={recent} />}
            {tab === "stats" && <StatsTab key="stats" stats={stats} />}
          </AnimatePresence>
        </div>

        <TabBar active={tab} onChange={setTab} />
      </div>
    </div>
  );
}

function AmbientBackground({ pulseKey }: { pulseKey: number }) {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 900px at 85% -20%, rgba(94,234,255,0.14), transparent 60%),radial-gradient(1100px 800px at 15% 120%, rgba(28,127,255,0.22), transparent 60%)",
        }}
      />
      <AnimatePresence>
        <motion.div
          key={pulseKey}
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.45, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          style={{
            background:
              "radial-gradient(800px 600px at 50% 50%, rgba(94,234,255,0.35), transparent 70%)",
          }}
        />
      </AnimatePresence>
    </>
  );
}

function TopBar({
  total,
  session,
  onSessionChange,
  pulseKey,
}: {
  total: number;
  session: SessionKey;
  onSessionChange: (s: SessionKey) => void;
  pulseKey: number;
}) {
  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-4">
        <PfizerLogo className="h-9" />
        <div className="h-8 w-px bg-white/10" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            You can build the future today
          </div>
          <div className="text-2xl font-semibold leading-none text-white">AI Teammate Builder</div>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-3">
        <motion.div
          key={pulseKey + "-counter"}
          initial={{ scale: 1 }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 0.6 }}
          className="glass glow-border flex items-center gap-3 rounded-full px-5 py-2"
        >
          <Users className="h-4 w-4 text-cyan-glow" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Teammates</div>
            <div className="font-display text-2xl leading-none text-white">{total}</div>
          </div>
        </motion.div>

        <div className="glass flex items-center gap-1 rounded-full p-1">
          {SESSION_KEYS.map((s) => (
            <button
              key={s}
              onClick={() => onSessionChange(s)}
              className={cn(
                "rounded-full px-4 py-1.5 text-xs font-medium transition",
                session === s
                  ? "bg-white text-ink-900"
                  : "text-white/60 hover:text-white",
              )}
            >
              {s === "all" ? "ALL" : `S${s}`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }) {
  return (
    <div className="mt-8 flex justify-center">
      <div className="glass flex items-center gap-1 rounded-full p-1.5">
        {TABS.map(({ id, label, icon: Icon }, idx) => (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={cn(
              "flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition",
              active === id
                ? "bg-white text-ink-900"
                : "text-white/60 hover:text-white",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            <span
              className={cn(
                "ml-1 rounded px-1.5 py-0.5 text-[10px]",
                active === id ? "bg-ink-900/10 text-ink-900/50" : "bg-white/5 text-white/40",
              )}
            >
              {idx + 1}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QRTab({ url, session }: { url: string; session: SessionKey }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2"
    >
      <div className="flex items-center justify-center">
        <QRBlock url={url} size={360} label="Scan with your phone camera" />
      </div>
      <div>
        <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          Session {session === "all" ? "ALL" : session}
        </div>
        <h1 className="mt-5 font-display text-7xl leading-[0.9] text-white">
          Build your<br />
          <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
            AI teammate
          </span>
        </h1>
        <p className="mt-6 max-w-lg text-xl leading-relaxed text-white/70">
          Take 3 minutes. Scan the QR code. Design an AI that takes the first 80% off one task in your job.
        </p>
        <ul className="mt-6 space-y-2 text-base text-white/80">
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> Pick one repetitive task
          </li>
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> Name your AI teammate
          </li>
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> Leave with a prompt you can use Monday morning
          </li>
        </ul>
      </div>
    </motion.div>
  );
}

function FeedTab({ recent }: { recent: TeammateSummary[] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-full bg-cyan-glow/10 px-3 py-1 text-xs text-cyan-glow">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-glow/80" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-glow" />
          </span>
          live feed
        </div>
        <div className="text-sm text-white/40">Latest AI teammates created right now</div>
      </div>

      <div className="no-scrollbar max-h-[64vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence initial={false}>
            {recent.map((t) => (
              <motion.a
                key={t.id}
                href={`/t/${t.id}`}
                target="_blank"
                rel="noopener noreferrer"
                layout
                initial={{ opacity: 0, y: 20, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -3, scale: 1.01 }}
                className="glass block rounded-lg p-5 transition-colors hover:bg-white/[0.06]"
                style={{ borderColor: `${t.accent}33` }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-2xl"
                    style={{ background: `${t.accent}22` }}
                  >
                    {t.emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-semibold text-white truncate">{t.name}</div>
                    <div className="text-xs text-white/50 capitalize">
                      {t.function} · {t.role}
                      {t.creatorName && <span className="ml-1 text-white/40">· by {t.creatorName}</span>}
                    </div>
                  </div>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-white/50">
                    S{t.sessionId}
                  </span>
                </div>
                <p className="mt-3 line-clamp-3 text-sm leading-snug text-white/75">
                  {t.mission}
                </p>
              </motion.a>
            ))}
          </AnimatePresence>
        </div>

        {recent.length === 0 && <EmptyState />}
      </div>
    </motion.div>
  );
}

function OrbTab({ pulseKey, total }: { pulseKey: number; total: number }) {
  const [intensity, setIntensity] = useState<"idle" | "pulse">("idle");
  useEffect(() => {
    if (!pulseKey) return;
    setIntensity("pulse");
    const t = setTimeout(() => setIntensity("idle"), 2000);
    return () => clearTimeout(t);
  }, [pulseKey]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex w-full flex-col items-center justify-center gap-10 text-center"
    >
      <Orb size={520} intensity={intensity} />
      <div>
        <div className="text-sm uppercase tracking-[0.3em] text-white/40">Pfizer AI presence</div>
        <h2 className="mt-3 font-display text-6xl leading-tight text-white">
          <span className="text-cyan-glow">{total}</span> minds building
        </h2>
        <p className="mt-4 max-w-xl text-lg text-white/60">
          Every pulse is a new teammate being born in this room.
        </p>
      </div>
    </motion.div>
  );
}

function MarketplaceTab({ recent }: { recent: TeammateSummary[] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-white">AI Teammate Marketplace</h2>
          <p className="text-sm text-white/60">Every teammate built today — browse, borrow, remix.</p>
        </div>
        <div className="text-xs text-white/40">{recent.length} shown</div>
      </div>
      <div className="no-scrollbar max-h-[62vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {recent.map((t) => (
            <motion.a
              key={t.id}
              href={`/t/${t.id}`}
              target="_blank"
              rel="noopener noreferrer"
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, scale: 1.015 }}
              className="glass relative block rounded-lg p-5 transition-colors hover:bg-white/[0.06]"
            >
              <div
                aria-hidden
                className="absolute -top-10 -right-10 h-32 w-32 rounded-full opacity-40 blur-2xl"
                style={{ background: `radial-gradient(circle, ${t.accent}, transparent 70%)` }}
              />
              <div className="relative">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl text-2xl"
                  style={{ background: `${t.accent}22` }}
                >
                  {t.emoji}
                </div>
                <div className="mt-3 text-lg font-semibold text-white">{t.name}</div>
                <div className="text-xs uppercase tracking-wide text-white/40">
                  {t.function}
                  {t.creatorName && <span className="normal-case ml-1 tracking-normal text-white/50">· by {t.creatorName}</span>}
                </div>
                <p className="mt-3 line-clamp-3 text-sm text-white/75">{t.mission}</p>
              </div>
            </motion.a>
          ))}
        </div>
        {recent.length === 0 && <EmptyState />}
      </div>
    </motion.div>
  );
}

function StatsTab({ stats }: { stats: Stats | null }) {
  const functionsSorted = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.byFunction)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stats]);

  const max = functionsSorted[0]?.[1] ?? 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Stat label="Teammates built" value={stats?.total ?? 0} highlight />
        <Stat label="Session 1" value={stats?.bySession?.["1"] ?? 0} />
        <Stat label="Session 2" value={stats?.bySession?.["2"] ?? 0} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">By function</h3>
          <div className="mt-4 space-y-3">
            {functionsSorted.length === 0 && <div className="text-sm text-white/40">Waiting for data…</div>}
            {functionsSorted.map(([f, n]) => (
              <div key={f}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="capitalize text-white/80">{f}</span>
                  <span className="font-mono text-white/60">{n}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(n / max) * 100}%` }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">Session 3</h3>
          <div className="mt-4 font-display text-7xl text-white">
            {stats?.bySession?.["3"] ?? 0}
          </div>
          <div className="mt-2 text-sm text-white/50">teammates built</div>
          <h3 className="mt-8 text-sm uppercase tracking-widest text-white/60">
            Popular names
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {(stats?.topNames ?? []).slice(0, 12).map((n) => (
              <span
                key={n.name}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80"
                style={{
                  fontSize: `${Math.min(22, 11 + n.count * 2)}px`,
                }}
              >
                {n.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={cn(
        "glass rounded-xl p-6",
        highlight && "glow-border",
      )}
    >
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-2 font-display text-6xl leading-none text-white">
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center text-white/40">
      <Orb size={100} intensity="idle" />
      <p className="mt-6 text-sm">Waiting for the first teammate…</p>
    </div>
  );
}

